import { defineStore } from 'pinia';
import PipelinesAPI from 'dashboard/api/pipelines';
import PipelineItemsAPI from 'dashboard/api/pipelineItems';

// Sante-CRM: estado do Kanban do funil odontológico.
// A movimentação de cards é otimista: a UI atualiza na hora e faz rollback
// (refetch) se a API rejeitar (ex.: permissão ou registro de outro tenant).
export const usePipelineStore = defineStore('pipeline', {
  state: () => ({
    pipelines: [],
    activePipelineId: null,
    items: [],
    uiFlags: {
      isFetching: false,
      isFetchingItems: false,
      isUpdating: false,
    },
  }),

  getters: {
    activePipeline: state =>
      state.pipelines.find(pipeline => pipeline.id === state.activePipelineId),
    stages() {
      const stages = this.activePipeline?.pipeline_stages || [];
      return [...stages].sort((a, b) => a.position - b.position);
    },
    itemsByStageId: state => stageId =>
      state.items
        .filter(item => item.pipeline_stage_id === stageId)
        .sort((a, b) => a.position - b.position),
  },

  actions: {
    async fetchPipelines() {
      this.uiFlags.isFetching = true;
      try {
        const response = await PipelinesAPI.get();
        this.pipelines = response.data.payload;
        if (!this.activePipelineId && this.pipelines.length) {
          const defaultPipeline =
            this.pipelines.find(pipeline => pipeline.is_default) ||
            this.pipelines[0];
          this.activePipelineId = defaultPipeline.id;
        }
      } finally {
        this.uiFlags.isFetching = false;
      }
    },

    async setActivePipeline(pipelineId) {
      this.activePipelineId = pipelineId;
      await this.fetchItems();
    },

    async fetchItems() {
      if (!this.activePipelineId) return;

      this.uiFlags.isFetchingItems = true;
      try {
        const response = await PipelineItemsAPI.get({
          pipelineId: this.activePipelineId,
        });
        this.items = response.data.payload;
      } finally {
        this.uiFlags.isFetchingItems = false;
      }
    },

    async createItem(itemAttributes) {
      const response = await PipelineItemsAPI.create(itemAttributes);
      this.items.push(response.data);
      return response.data;
    },

    // Move o card para outra coluna/posição (drag & drop do Kanban).
    async moveItem({ itemId, stageId, position }) {
      const item = this.items.find(({ id }) => id === itemId);
      if (!item) return;

      const previousState = this.items.map(existing => ({ ...existing }));

      item.pipeline_stage_id = stageId;
      item.position = position;
      this.resequenceStage(stageId, itemId, position);

      this.uiFlags.isUpdating = true;
      try {
        const response = await PipelineItemsAPI.update(itemId, {
          pipeline_stage_id: stageId,
          position,
        });
        Object.assign(item, response.data);
      } catch (error) {
        this.items = previousState;
        throw error;
      } finally {
        this.uiFlags.isUpdating = false;
      }
    },

    async deleteItem(itemId) {
      await PipelineItemsAPI.delete(itemId);
      this.items = this.items.filter(({ id }) => id !== itemId);
    },

    // Reordena localmente as posições da coluna após soltar um card.
    resequenceStage(stageId, movedItemId, movedPosition) {
      const stageItems = this.items
        .filter(
          item =>
            item.pipeline_stage_id === stageId && item.id !== movedItemId
        )
        .sort((a, b) => a.position - b.position);

      stageItems.forEach((item, index) => {
        item.position = index >= movedPosition ? index + 1 : index;
      });
    },
  },
});
